/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useEffect, useState, useRef } from "react";
import {
  Form,
  Input,
  Radio,
  Button,
  Spin,
  message,
  InputNumber,
  Card,
  Space,
  Badge,
} from "antd";
import {
  CarOutlined,
  UserOutlined,
  SafetyOutlined,
  SendOutlined,
  CheckOutlined,
  LoadingOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../UserContext";
import { useSettings } from "../../../SettingsContext";
import {
  fetchPreOperaciones,
  generatePreOperacionPDF,
  fetchAnsweredToday,
  setAnsweredToday,
} from "../../../service/preOperacionService";
import api from "../../../axiosConfig";
import logo from "../../../Assets/img/logo.webp";
import {
  UnifiedContainer,
  UnifiedLogo,
  UnifiedCardLight,
  UnifiedTitle,
  UnifiedLoadingContainer,
  CenteredLayout,
  colors,
  fadeIn
} from "../../../theme/SharedDesign";
import { Typography as AntTypography } from 'antd';

const { TextArea } = Input;
const { Text } = AntTypography;

// Componente para el aviso obligatorio
const WarningBox = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 152, 0, 0.1)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 24,
  border: '2px solid rgba(255, 152, 0, 0.3)',
}));

function PreOperationalForm() {
  const { user } = useUser();
  const { setIsPreoperationalActive } = useSettings();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [canSubmit, setCanSubmit] = useState(false);
  const [form] = Form.useForm();
  const [vehiculoPrincipal, setVehiculoPrincipal] = useState(null);
  const [ubicacion, setUbicacion] = useState(null);
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasLoadedRef = useRef(false);

  // Marcar formulario como activo al montar, desmarcar al desmontar
  useEffect(() => {
    setIsPreoperationalActive(true);

    return () => {
      setIsPreoperationalActive(false);
      hasLoadedRef.current = false;
    };
  }, [setIsPreoperationalActive]);

  // Bloquear navegación hasta completar formulario
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Debes completar el formulario preoperacional antes de continuar.';
      return 'Debes completar el formulario preoperacional antes de continuar.';
    };

    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, null, window.location.pathname);
      message.warning('Debes completar el formulario preoperacional antes de continuar.');
    };

    // Agregar event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Prevenir navegación con el botón atrás
    window.history.pushState(null, null, window.location.pathname);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Cargar preguntas al montar el componente (solo una vez)
  useEffect(() => {
    if (!user?.id) {
      setIsPreoperationalActive(false);
      navigate('/modules/Dashboard');
      return;
    }

    // Evitar doble ejecución cuando user cambia de referencia (ej: UserContext agrega vehiculoPrincipal)
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;

    const loadPreOperational = async () => {
      try {
        setInitialLoading(true);

        // Obtener información del vehículo principal
        const vehiculoResponse = await api.get("/conductor/vehiculo-principal", {
          params: { conductor_id: user.id },
        });

        // Manejar diferentes formatos de respuesta
        let principal = null;
        const responseData = vehiculoResponse.data;

        if (responseData?.success && responseData?.data) {
          // Formato: { success: true, data: { ... } }
          principal = responseData.data;
        } else if (Array.isArray(responseData)) {
          // Formato legacy: array de vehículos
          principal = responseData.find((v) => v.es_principal);
        } else if (responseData?.es_principal !== undefined) {
          // Formato: objeto directo
          principal = responseData;
        }

        if (!principal || !principal.vehiculo) {
          // Sin vehículo asignado: redirigir silenciosamente al Dashboard
          // El conductor puede ver sus traslados históricos sin preoperacional
          setIsPreoperationalActive(false);
          navigate('/modules/Dashboard');
          return;
        }

        const principalFormateado = {
          ...principal,
          Vehiculo: principal.vehiculo
        };
        setVehiculoPrincipal(principalFormateado);

        // Si el conductor o el vehículo no generan preoperacional, el formulario no aplica (Anexo A).
        // Si la consulta falla se sigue como antes (se exige).
        try {
          const perfilResponse = await api.get("/conductor/perfil");
          if (perfilResponse.data?.data?.requiere_preoperacional === false) {
            setIsPreoperationalActive(false);
            navigate('/modules/Dashboard');
            return;
          }
        } catch (perfilError) {
          console.warn("No se pudo consultar si aplica el preoperacional:", perfilError);
        }

        // Verificar si ya completó el preoperacional hoy
        const hasAnswered = await fetchAnsweredToday(user.id, principal.vehiculo_id);
        if (hasAnswered) {
          // Navegar silenciosamente sin mostrar mensaje
          setIsPreoperationalActive(false);
          navigate('/modules/Dashboard');
          return;
        }

        // Cargar preguntas
        const questionsData = await fetchPreOperaciones();

        // Inicializar valores por defecto
        const initialValues = {};
        questionsData.forEach((q) => {
          if (q.tipoRespuesta === "checklist_multiple") {
            initialValues[q.id] = Array(q.descripciones?.length || 0).fill(null);
          } else {
            initialValues[q.id] = "";
          }
        });

        form.setFieldsValue(initialValues);
        setQuestions(questionsData);

      } catch (error) {
        console.error("Error al cargar preoperacional:", error);
        message.error("Error al cargar el formulario preoperacional.");
        setIsPreoperationalActive(false);
        navigate('/modules/Dashboard');
        hasLoadedRef.current = false;
      } finally {
        setInitialLoading(false);
      }
    };

    loadPreOperational();
  }, [user, navigate, form]);

  // Verifica si todas las preguntas tienen respuesta
  const checkComplete = (values) => {
    if (!questions.length) return false;

    return questions.every((q) => {
      const v = values[q.id];

      if (q.tipoRespuesta === "checklist_multiple") {
        return q.descripciones.every((_, i) => {
          const ans = v?.[i];
          return Array.isArray(ans) ? ans.length > 0 : typeof ans === "string" && ans !== "";
        });
      }

      return v !== undefined && v !== "";
    });
  };

  // Actualizar respuestas
  const onValuesChange = (_, allValues) => {
    localStorage.setItem(`preop_${user.id}`, JSON.stringify(allValues));
    setCanSubmit(checkComplete(allValues));
  };

  // Obtener ubicación actual
  const obtenerUbicacion = () => {
    if (!navigator.geolocation) {
      message.warning("Tu navegador no soporta geolocalización.");
      return Promise.resolve(null);
    }

    setObteniendoUbicacion(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          try {
            const coords = {
              latitud: position.coords.latitude,
              longitud: position.coords.longitude,
              precision: position.coords.accuracy,
              timestamp: new Date().toISOString(),
            };
            setUbicacion(coords);
            setObteniendoUbicacion(false);
            resolve(coords);
          } catch (error) {
            console.error("Error al procesar coordenadas:", error);
            setObteniendoUbicacion(false);
            resolve(null);
          }
        },
        (error) => {
          message.info("No se pudo obtener la ubicación exacta. Continuando sin ella.");
          setObteniendoUbicacion(false);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  // Enviar formulario
  const handleSubmit = async () => {
    if (!canSubmit) {
      message.warning("Por favor completa todas las preguntas antes de continuar.");
      return;
    }

    if (!vehiculoPrincipal || !vehiculoPrincipal.vehiculo_id) {
      // Sin vehículo: redirigir al Dashboard
      navigate('/modules/Dashboard');
      return;
    }

    setLoading(true);
    try {
      await form.validateFields();
      const ubicacionActual = await obtenerUbicacion();
      const values = form.getFieldsValue(true);

      const respuestas = questions.flatMap((q) => {
        const v = values[q.id];

        if (v === undefined || v === null || v === "") {
          return [];
        }

        if (q.tipoRespuesta === "checklist_multiple" && Array.isArray(q.descripciones)) {
          return q.descripciones.map((d, i) => {
            if (v[i] === undefined || v[i] === null) {
              return {
                pregunta: `${q.titulo} - ${d.label}`,
                respuesta: "No contestada",
              };
            }

            return {
              pregunta: `${q.titulo} - ${d.label}`,
              respuesta: Array.isArray(v[i]) ? v[i].join(", ") : v[i],
            };
          });
        }

        return [{
          pregunta: q.titulo,
          respuesta: Array.isArray(v) ? v.join(", ") : v,
        }];
      });

      await generatePreOperacionPDF({
        conductorId: user.id,
        vehiculoId: vehiculoPrincipal.vehiculo_id,
        respuestas,
        ubicacion: ubicacionActual,
      });

      setAnsweredToday(user.id);
      localStorage.removeItem(`preop_${user.id}`);
      setIsPreoperationalActive(false); // Desmarcar antes de navegar
      message.success("Formulario preoperacional completado correctamente.");
      navigate('/modules/Dashboard');

    } catch (error) {
      console.error("Error al enviar formulario:", error);
      // Si el backend detectó que ya fue completado hoy (409), redirigir al Dashboard
      if (error.response?.status === 409 || error.response?.data?.already_completed) {
        setAnsweredToday(user.id);
        localStorage.removeItem(`preop_${user.id}`);
        setIsPreoperationalActive(false);
        message.info("El formulario preoperacional ya fue completado hoy.");
        navigate('/modules/Dashboard');
        return;
      }
      const errorMsg = error.response?.data?.message || error.message || "Error al enviar el formulario.";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  if (initialLoading) {
    return (
      <UnifiedContainer>
        <CenteredLayout isMobile={isMobile} maxWidth={800}>
          <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
          
          <UnifiedLoadingContainer>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#0a2e5c' }} spin />} />
            <Typography sx={{ 
              color: 'rgba(10, 46, 92, 0.8)', 
              fontSize: '1rem', 
              fontWeight: 500,
              position: 'relative',
              zIndex: 1
            }}>
              Cargando formulario preoperacional...
            </Typography>
          </UnifiedLoadingContainer>
        </CenteredLayout>
      </UnifiedContainer>
    );
  }

  return (
    <UnifiedContainer>
      <CenteredLayout isMobile={isMobile} maxWidth={800}>
        <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
        
        <UnifiedCardLight sx={{ width: '100%', position: 'relative', zIndex: 1 }} isMobile={isMobile}>

          <UnifiedTitle variant="h4" isMobile={isMobile}>
            <SafetyOutlined sx={{ fontSize: isMobile ? '1.6rem' : '2rem', color: '#0a2e5c' }} />
            <span style={{ color: '#0a2e5c' }}>Formulario Pre-Operacional</span>
          </UnifiedTitle>

          {loading || obteniendoUbicacion ? (
            <UnifiedLoadingContainer>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#0a2e5c' }} spin />} />
              <Text style={{ 
                color: 'rgba(10, 46, 92, 0.8)',
                fontSize: '1rem',
                fontWeight: 500,
                position: 'relative',
                zIndex: 1
              }}>
                {loading ? "Enviando formulario..." : "Obteniendo ubicación..."}
              </Text>
            </UnifiedLoadingContainer>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onValuesChange={onValuesChange}
              requiredMark="optional"
              style={{ position: 'relative', zIndex: 1 }}
            >
              {vehiculoPrincipal && (
                <Card 
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 12,
                    marginBottom: 24,
                    border: '1px solid rgba(10, 46, 92, 0.2)',
                  }} 
                  bordered={false}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space align="center">
                      <UserOutlined style={{ fontSize: 18, color: "#0599ee" }} />
                      <Text strong>Conductor:</Text>
                      <Text>{user.nombre}</Text>
                    </Space>

                    <Space align="center">
                      <CarOutlined style={{ fontSize: 18, color: "#0599ee" }} />
                      <Text strong>Vehículo asignado:</Text>
                      <Text>
                        Código {vehiculoPrincipal.Vehiculo?.codigo}, Placa{" "}
                        {vehiculoPrincipal.Vehiculo?.placa}
                      </Text>
                    </Space>

                    <Space align="center">
                      <EnvironmentOutlined style={{ fontSize: 18, color: "#0599ee" }} />
                      <Text strong>Ubicación:</Text>
                      <Text>
                        {ubicacion ? "Disponible" : "Se obtendrá al enviar"}
                      </Text>
                    </Space>
                  </Space>
                </Card>
              )}

              <WarningBox>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space align="center">
                    <SafetyOutlined style={{ color: "#ff9800", fontSize: '1.2rem' }} />
                    <Text style={{ color: '#0a2e5c', fontWeight: 600, fontSize: '1rem' }}>
                      FORMULARIO OBLIGATORIO
                    </Text>
                  </Space>
                  <Text style={{ color: 'rgba(10, 46, 92, 0.8)', fontWeight: 400, fontSize: '0.9rem' }}>
                    Debes completar todas las preguntas para poder acceder al sistema.
                    No podrás continuar hasta finalizar este formulario.
                  </Text>
                </Space>
              </WarningBox>

              {questions.map((q, qIndex) => (
                <Card
                  key={q.id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 12,
                    marginBottom: 16,
                    border: '1px solid rgba(10, 46, 92, 0.2)',
                  }}
                  title={
                    <Space>
                      <Badge count={qIndex + 1} style={{ backgroundColor: "#0599ee" }} />
                      <Text strong>{q.titulo}</Text>
                    </Space>
                  }
                >
                  {q.tipoRespuesta === "texto" && (
                    <Form.Item
                      name={q.id}
                      rules={[{ required: true, message: "Campo requerido" }]}
                    >
                      <TextArea
                        rows={3}
                        placeholder="Escribe tu respuesta aquí..."
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  )}

                  {q.tipoRespuesta === "numerico" && (
                    <Form.Item
                      name={q.id}
                      rules={[
                        { required: true, message: "Campo requerido" },
                        { type: "number", message: "Debe ser un número válido" },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%", borderRadius: 6 }}
                        min={0}
                        placeholder="Ingresa un valor numérico"
                      />
                    </Form.Item>
                  )}

                  {q.tipoRespuesta === "checklist_simple" && (
                    <Form.Item
                      name={q.id}
                      rules={[{ required: true, message: "Selecciona una opción" }]}
                    >
                      <Radio.Group style={{ width: "100%" }}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {q.opciones.map((opt) => (
                            <Card
                              key={`${q.id}_${opt}`}
                              style={{
                                marginBottom: 8,
                                borderColor: "#d9d9d9",
                                cursor: "pointer",
                                transition: "all 0.3s",
                              }}
                              hoverable
                              size="small"
                              styles={{ body: { padding: "12px 16px" } }}
                              onClick={() => {
                                const currentValues = form.getFieldsValue();
                                form.setFieldsValue({
                                  ...currentValues,
                                  [q.id]: opt,
                                });
                                onValuesChange(null, {
                                  ...currentValues,
                                  [q.id]: opt,
                                });
                              }}
                            >
                              <Radio value={opt} style={{ width: "100%" }}>
                                {opt}
                              </Radio>
                            </Card>
                          ))}
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                  )}

                  {q.tipoRespuesta === "checklist_multiple" && (
                    <Form.Item required>
                      <div style={{ marginBottom: 16 }}>
                        {q.descripciones.map((d, i) => (
                          <div
                            key={`${q.id}_desc_${i}`}
                            style={{
                              backgroundColor: "#fff",
                              borderRadius: 8,
                              marginBottom: 12,
                              border: "1px solid #f0f0f0",
                              overflow: "hidden",
                            }}
                          >
                            <div style={{
                              backgroundColor: "rgba(10, 46, 92, 0.05)",
                              padding: "10px 12px",
                              fontWeight: 500,
                              borderBottom: "1px solid #f0f0f0",
                            }}>
                              {d.label}
                            </div>
                            <div style={{
                              padding: "8px 12px",
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}>
                              <Text style={{
                                fontSize: 14,
                                color: "rgba(0,0,0,0.65)",
                                marginBottom: 4,
                              }}>
                                Selecciona una opción:
                              </Text>
                              <Form.Item
                                name={[q.id, i]}
                                fieldKey={[q.id, i]}
                                rules={[{ required: true, message: "Selecciona una opción" }]}
                                style={{ margin: 0 }}
                              >
                                <Radio.Group style={{ width: "100%" }}>
                                  <Space direction="vertical" style={{ width: "100%" }}>
                                    {d.options.map((opt) => (
                                      <Card
                                        key={`${q.id}_${i}_${opt}`}
                                        style={{
                                          marginBottom: 6,
                                          borderColor: "#d9d9d9",
                                          cursor: "pointer",
                                          transition: "all 0.3s",
                                        }}
                                        hoverable
                                        size="small"
                                        styles={{ body: { padding: "10px 12px" } }}
                                        onClick={() => {
                                          const currentValues = form.getFieldsValue();
                                          const currentMultiValues = currentValues[q.id] || [];
                                          const updatedValues = [...currentMultiValues];
                                          updatedValues[i] = opt;
                                          form.setFieldsValue({
                                            ...currentValues,
                                            [q.id]: updatedValues,
                                          });
                                          onValuesChange(null, {
                                            ...currentValues,
                                            [q.id]: updatedValues,
                                          });
                                        }}
                                      >
                                        <Radio value={opt} style={{ width: "100%" }}>
                                          {opt}
                                        </Radio>
                                      </Card>
                                    ))}
                                  </Space>
                                </Radio.Group>
                              </Form.Item>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Form.Item>
                  )}
                </Card>
              ))}

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  block
                  icon={canSubmit ? <SendOutlined /> : <CheckOutlined />}
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                  style={{
                    height: 48,
                    fontSize: 16,
                    borderRadius: 8,
                    background: canSubmit 
                      ? "linear-gradient(135deg, #0599ee, #0a2e5c)" 
                      : "rgba(0, 0, 0, 0.25)",
                    borderColor: canSubmit ? "#0a2e5c" : "rgba(0, 0, 0, 0.25)",
                  }}
                >
                  {canSubmit ? "Enviar Formulario" : "Complete todas las preguntas"}
                </Button>
              </Form.Item>
            </Form>
          )}
        </UnifiedCardLight>
      </CenteredLayout>
    </UnifiedContainer>
  );
}

export default PreOperationalForm;